import React, { useState, useEffect } from 'react';
import AnimateOnScroll from '../../about/Components/AnimateOnScroll';
import { useNavigate } from 'react-router-dom';
const PricingPage = () => {
  const navigate = useNavigate();
  
  // State to track viewport width for responsive adjustments
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Colors constants based on PlasticAnalytics branding
  const colors = {
    primary: '#FFFFFF',
    blue: 'rgb(26, 42, 108)',
    orange: 'rgb(230, 126, 34)',
    text: '#333333',
    lightBlue: 'rgba(26, 42, 108, 0.1)',
    lightOrange: 'rgba(230, 126, 34, 0.1)',
    border: '#E5E7EB',
    darkBlue: 'rgb(20, 32, 82)',
    darkOrange: 'rgb(200, 110, 30)'
  };
  
  // Update window width state when resizing
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Responsive helper functions
  const isMobile = windowWidth <= 640;
  const isTablet = windowWidth > 640 && windowWidth <= 1024;
  const isDesktop = windowWidth > 1024;
  
  // Get container padding based on screen size
  const getContainerPadding = () => {
    if (isMobile) return '40px 16px';
    if (isTablet) return '50px 24px';
    return '60px 20px';
  };
  
  // Get card padding based on screen size
  const getCardPadding = () => {
    if (isMobile) return '30px 20px';
    if (isTablet) return '35px 25px';
    return '40px';
  };
  
  // Get font size for price based on screen size
  const getPriceSize = () => {
    if (isMobile) return '2rem';
    if (isTablet) return '2.25rem';
    return '2.5rem';
  };

  // Button components to avoid repetition
  const PrimaryButton = ({ text, isOrange = false, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    
    return (
      <button 
        style={{ 
          border: '2px solid white',
          borderRadius: '8px',
          padding: isMobile ? '10px 18px' : '12px 24px',
          backgroundColor: 'white',
          color: isOrange ? colors.orange : colors.blue,
          cursor: 'pointer',
          marginBottom: '25px',
          fontWeight: '600',
          fontSize: isMobile ? '0.9rem' : '1rem',
          boxShadow: isHovered ? '0 6px 16px rgba(0, 0, 0, 0.2)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
          transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
          transition: 'all 0.3s ease',
          width: isMobile ? '100%' : 'auto'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onClick}
      >
        {text}
      </button>
    );
  };
  
  const SecondaryButton = ({ text, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    
    return (
      <button 
        style={{ 
          border: `2px solid ${colors.blue}`,
          borderRadius: '8px',
          padding: isMobile ? '10px 18px' : '12px 24px',
          backgroundColor: isHovered ? colors.lightBlue : 'transparent',
          color: colors.blue,
          cursor: 'pointer',
          marginBottom: '25px',
          fontWeight: '600',
          fontSize: isMobile ? '0.9rem' : '1rem',
          transition: 'all 0.3s ease',
          width: isMobile ? '100%' : 'auto'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onClick}
      >
        {text}
      </button>
    );
  };
  
  const ContactButton = ({ onClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    
    return (
      <button 
        style={{ 
          border: 'none',
          borderRadius: '8px',
          padding: isMobile ? '12px 24px' : '14px 28px',
          backgroundColor: isHovered ? 'rgb(36, 52, 128)' : colors.blue,
          color: 'white',
          cursor: 'pointer',
          fontWeight: '600',
          fontSize: isMobile ? '0.9rem' : '1rem',
          boxShadow: isHovered ? '0 6px 16px rgba(26, 42, 108, 0.3)' : '0 4px 12px rgba(26, 42, 108, 0.2)',
          transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
          transition: 'all 0.3s ease',
          width: isMobile ? '100%' : 'auto'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => navigate('/contact-us')}
      >
        Contact Us
      </button>
    );
  };
  
  // Feature list component to avoid repetition
  const FeatureList = ({ features, isBlue = false, isOrange = false }) => {
    const getCircleClass = () => {
      if (isBlue) return "blue";
      if (isOrange) return "orange";
      return "white";
    };
    
    return (
      <ul style={{ 
        listStyleType: 'none', 
        padding: 0,
        textAlign: 'left',
        marginTop: 0,
        flex: '1',
        color: isBlue ? colors.text : 'white'
      }}>
        {features.map((feature, index) => (
          <li key={index} className="feature-item">
            <div className={`check-circle ${getCircleClass()}`}></div>
            <span>{feature.highlighted ? <strong>{feature.text}</strong> : feature.text}</span>
          </li>
        ))}
      </ul>
    );
  };
  
  // Features for each tier
  const freeFeatures = [
    { text: "Basic correlation analysis" },
    { text: "3 categorisations using CNN model" },
    { text: "Basic spectral library access" },
    { text: "Limited microplastics identification" },
    { text: "Data exports in CSV format" },
    { text: "Community support" },
    { text: "1 user account" }
  ];
  
  const individualFeatures = [
    { text: "Full access to CNN model", highlighted: true },
    { text: "Advanced image analysis tools", highlighted: true },
    { text: "Unlimited categorisations" },
    { text: "Complete spectral library" },
    { text: "Advanced data visualisations" },
    { text: "Export in multiple formats" },
    { text: "Priority email support" },
    { text: "2 user accounts" }
  ];
  
  const commercialFeatures = [
    { text: "Advanced computer vision algorithms", highlighted: true },
    { text: "Spectral imaging API integration with third-party spectrometers", highlighted: true },
    { text: "Upto 30 team members" },
    { text: "Comprehensive spectral imaging analysis suite" },
    { text: "Process spectral imaging in minutes (vs. hours)" },
    { text: "Advanced analytics dashboard" },
    { text: "Options for AI model customisation" },
  ];
  
  // Create a custom ordering for tablet view to optimize layout
  const getTabletOrder = (card) => {
    // For tablet, place the featured card in the middle
    switch(card) {
      case 'free': return 1;
      case 'individual': return 2;
      case 'commercial': return 3;
    }
  };
  
  // Get card transform based on card type and device
  const getCardTransform = (cardType) => {
    if (cardType === 'individual') {
      if (isDesktop) return 'translateY(-8px)';
      if (isTablet) return 'scale(1.05)';
    }
    return 'none';
  };
  
  // Get card shadow based on card type and device
  const getCardShadow = (cardType) => {
    if (cardType === 'individual') {
      if (isTablet) return '0 12px 28px rgba(26, 42, 108, 0.30)';
      return '0 8px 24px rgba(26, 42, 108, 0.25)';
    }
    if (cardType === 'commercial') {
      return '0 4px 12px rgba(230, 126, 34, 0.25)';
    }
    return '0 4px 12px rgba(0, 0, 0, 0.05)';
  };
  
  return (
    <AnimateOnScroll>
      <div style={{ 
        color: colors.text,
        fontFamily: 'Helvetica, sans-serif',
        padding: getContainerPadding(),
        backgroundColor: colors.primary
      }}>
        <div className="pricing-container">
          {/* Free Tier */}
          <div className="pricing-column" style={{ order: isTablet ? getTabletOrder('free') : 'unset' }}>
            <div style={{
              padding: getCardPadding(),
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: getCardShadow('free'),
              border: `1px solid ${colors.border}`,
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <h2 style={{ 
                fontSize: isMobile ? '1.5rem' : '1.75rem', 
                fontWeight: '600', 
                marginBottom: '10px',
                color: colors.blue 
              }}>
                Free
              </h2>
              <p style={{ 
                fontSize: getPriceSize(),
                fontWeight: '700',
                marginBottom: '5px',
                color: colors.blue
              }}>
                €0
              </p>
              <p style={{ 
                color: colors.text,
                marginBottom: '30px',
                fontSize: isMobile ? '0.9rem' : '1rem',
                opacity: 0.8
              }}>
                Always free for basic research
              </p>
              
              <SecondaryButton text="Get Started" onClick={() => {}} />
              
              <div style={{ 
                borderTop: `1px solid ${colors.border}`,
                paddingTop: '20px',
                marginBottom: '20px'
              }}>
                <p style={{
                  fontWeight: '600',
                  textAlign: 'left'
                }}>
                  Basic features include:
                </p>
              </div>
              
              <FeatureList features={freeFeatures} isBlue={true} />
            </div>
          </div>

          {/* Individual License Tier */}
          <div 
            className="pricing-column featured" 
            style={{ 
              order: isTablet ? getTabletOrder('individual') : 'unset',
              zIndex: 2 // Keep above other cards for shadow effect
            }}
          >
            <div style={{
              padding: getCardPadding(),
              backgroundColor: colors.blue,
              borderRadius: '12px',
              boxShadow: getCardShadow('individual'),
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              transform: getCardTransform('individual'),
              zIndex: 1,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }}>
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#fff',
                color: colors.blue,
                padding: '4px 16px',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '700',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
              }}>
                MOST POPULAR
              </div>
              
              <h2 style={{ 
                fontSize: isMobile ? '1.5rem' : '1.75rem', 
                fontWeight: '600', 
                marginBottom: '10px',
                color: 'white'
              }}>
                Individual License
              </h2>
              <p style={{ 
                fontSize: getPriceSize(),
                fontWeight: '700',
                marginBottom: '5px',
                color: 'white'
              }}>
                €35
              </p>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: '30px',
                fontSize: isMobile ? '0.9rem' : '1rem'
              }}>
                per month
              </p>
              
              <PrimaryButton text="Subscribe Now" onClick={() => {}} />
              
              <div style={{ 
                borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                paddingTop: '20px',
                marginBottom: '20px'
              }}>
                <p style={{
                  fontWeight: '600',
                  textAlign: 'left',
                  color: 'white'
                }}>
                  Everything in Free, plus:
                </p>
              </div>
              
              <FeatureList features={individualFeatures} />
            </div>
          </div>

          {/* Commercial License Tier */}
          <div className="pricing-column" style={{ order: isTablet ? getTabletOrder('commercial') : 'unset' }}>
            <div style={{
              padding: getCardPadding(),
              backgroundColor: colors.orange,
              borderRadius: '12px',
              boxShadow: getCardShadow('commercial'),
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <h2 style={{ 
                fontSize: isMobile ? '1.5rem' : '1.75rem', 
                fontWeight: '600', 
                marginBottom: '10px',
                color: 'white'
              }}>
                Commercial License
              </h2>
              <p style={{ 
                fontSize: getPriceSize(),
                fontWeight: '700',
                marginBottom: '5px',
                color: 'white'
              }}>
                €5,000
              </p>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: '30px',
                fontSize: isMobile ? '0.9rem' : '1rem'
              }}>
                per year
              </p>
              
              <PrimaryButton text="Contact Sales" isOrange={true} onClick={() => {}} />
              
              <div style={{ 
                borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                paddingTop: '20px',
                marginBottom: '20px'
              }}>
                <p style={{
                  fontWeight: '600',
                  textAlign: 'left',
                  color: 'white'
                }}>
                  Everything in Individual License, plus:
                </p>
              </div>
              
              <FeatureList features={commercialFeatures} />
            </div>
          </div>
        </div>

        <div style={{
          maxWidth: isMobile ? '100%' : '800px',
          margin: '60px auto 0',
          textAlign: 'center',
          padding: isMobile ? '24px 16px' : '30px',
          backgroundColor: colors.lightBlue,
          borderRadius: '12px'
        }}>
          <h3 style={{
            fontSize: isMobile ? '1.3rem' : '1.5rem',
            fontWeight: '600',
            color: colors.blue,
            marginBottom: '15px'
          }}>
            Need a custom solution?
          </h3>
          <p style={{
            fontSize: isMobile ? '1rem' : '1.125rem',
            marginBottom: '25px'
          }}>
            Contact our team for custom research collaborations, educational institution packages, 
            and specialized industry solutions.
          </p>
          <ContactButton onClick={() => navigate('/contact-us')} />
        </div>

        <style>{`
          .pricing-container {
            display: grid;
            grid-template-columns: ${isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'};
            gap: ${isMobile ? '24px' : '30px'};
            max-width: 1200px;
            margin: 0 auto;
          }
          
          .pricing-column {
            display: flex;
            flex-direction: column;
          }
          
          .pricing-column.featured {
            ${isTablet ? `
              grid-column: span 2;
              max-width: 500px;
              margin: 0 auto;
            ` : ''}
          }
          
          .feature-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: ${isMobile ? '14px' : '16px'};
            line-height: 1.5;
            font-size: ${isMobile ? '0.9rem' : '1rem'};
          }
          
          .check-circle {
            width: ${isMobile ? '18px' : '20px'};
            height: ${isMobile ? '18px' : '20px'};
            border-radius: 50%;
            margin-right: 12px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            margin-top: 2px;
          }
          
          .check-circle::after {
            content: '';
            display: block;
            width: ${isMobile ? '7px' : '8px'};
            height: ${isMobile ? '4px' : '5px'};
            border-left: 2px solid white;
            border-bottom: 2px solid white;
            transform: rotate(-45deg);
            position: absolute;
            top: ${isMobile ? '5px' : '6px'};
          }
          
          .blue {
            background-color: rgb(26, 42, 108);
          }
          
          .orange {
            background-color: rgb(230, 126, 34);
          }
          
          .white {
            background-color: rgba(255, 255, 255, 0.3);
          }
          
          @media (max-width: 340px) {
            .feature-item {
              font-size: 0.85rem;
            }
          }
        `}</style>
      </div>
    </AnimateOnScroll>
  );
};

export default PricingPage;